import React from 'react';
import { Box, Container, Typography, Grid, Link, IconButton, Divider } from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import { landingColors } from '../../../theme/landingTheme';

const footerLinks = {
  product: [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Integrations', href: '#' },
    { label: 'API', href: '#' },
  ],
  company: [
    { label: 'About', href: '#' },
    { label: 'Blog', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Press', href: '#' },
  ],
  resources: [
    { label: 'Documentation', href: '#' },
    { label: 'Help Center', href: '#' },
    { label: 'Community', href: '#' },
    { label: 'Status', href: '#' },
  ],
  legal: [
    { label: 'Privacy', href: '#' },
    { label: 'Terms', href: '#' },
    { label: 'Cookie Policy', href: '#' },
    { label: 'Licenses', href: '#' },
  ],
};

const socialLinks = [
  { icon: TwitterIcon, href: '#', label: 'Twitter' },
  { icon: LinkedInIcon, href: '#', label: 'LinkedIn' },
  { icon: GitHubIcon, href: '#', label: 'GitHub' },
  { icon: EmailIcon, href: 'mailto:hello@nexera.com', label: 'Email' },
];

export const Footer: React.FC = () => {
  const scrollToSection = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        pt: { xs: 8, md: 12 },
        pb: 4,
        backgroundColor: landingColors.textPrimary,
        color: '#fff',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                  }}
                >
                  S
                </Box>
              </Box>
              <Box
                sx={{
                  fontFamily: '"Cabinet Grotesk", sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: '#fff',
                  letterSpacing: '-0.02em',
                }}
              >
                Store<span style={{ color: landingColors.primaryLight }}>Sphere</span>
              </Box>
            </Box>

            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9375rem',
                lineHeight: 1.7,
                mb: 3,
                maxWidth: 280,
              }}
            >
              The complete platform for modern retail. Manage inventory, sales, and
              customers all in one beautiful dashboard.
            </Typography>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <IconButton
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        color: '#fff',
                        backgroundColor: landingColors.primary,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </IconButton>
                );
              })}
            </Box>
          </Grid>

          {/* Links Columns */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={4}>
              {Object.entries(footerLinks).map(([category, links]) => (
                <Grid item xs={6} sm={3} key={category}>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 2,
                    }}
                  >
                    {category}
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {links.map((link, index) => (
                      <Box component="li" key={index} sx={{ mb: 1.5 }}>
                        <Link
                          href={link.href}
                          onClick={(e) => {
                            if (link.href.startsWith('#')) {
                              e.preventDefault();
                              scrollToSection(link.href);
                            }
                          }}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.9375rem',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                              color: '#fff',
                            },
                          }}
                        >
                          {link.label}
                        </Link>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Divider
          sx={{
            my: 6,
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.875rem',
            }}
          >
            © {new Date().getFullYear()} Nexera. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': { color: '#fff' },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': { color: '#fff' },
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
